import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import TextInput from '../../widgets/components/Input/TextInput'
import { investorType } from '../core/_constants'
import Select from 'react-select'
import { CustomButton } from '../../widgets/components/UI/CustomButton'
import { createInvestor } from '../core/_requests'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'

const initialValues = {
  name: '',
  contact_name: '',
  email: '',
}
export const CreateInvestorModal = ({ showModal, setShowModal }: any) => {
  const { formatMessage } = useIntl()
  const [investorsType, setInvestorsType] = useState<any>([])
  const [loading, setLoading] = useState(false)
  const [fundTypeError, setFundTypeError] = useState(false)
  const [status, setStatus] = useState(false)
  const navigate = useNavigate()

  const userSchema = Yup.object().shape({
    name: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Investor name/firm name is required' }))
      .nullable(),
    contact_name: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Contact name is required' }))
      .nullable(),
    email: Yup.string()
      .email(formatMessage({ id: 'Invalid email format' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Email is required' })),
  })
  const { companyId, currentUser } = useAuth()
  const onSubmit = async (values: any) => {
    setLoading(true)
    setStatus(true)
    try {
      let fund_type = investorsType.map((e: any) => e.value)

      if (fund_type.length > 0 && companyId) {
        const payload = {
          ...values,
          fund_type,
          companyId: companyId,
        }
        const {
          data: { data, success, errors },
        } = await createInvestor(payload)
        if (success) {
          setShowModal(false)
          setLoading(false)
          toast.success(formatMessage({ id: 'Investor added successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddInvestorSuccessful,
              action: 'normal',
              metadata: {
                fund_type,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                data: values,
              },
            },
          })
          navigate(`/investor-database/individual-investor/${data?.investorId}`)
        } else {
          setLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddInvestorFailed,
              action: 'normal',
              metadata: {
                fund_type,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                data: values,
                error: errors[0],
              },
            },
          })
        }
      } else {
        setFundTypeError(true)
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.AddInvestorFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            data: values,
            error: err,
          },
        },
      })
    }
  }
  useEffect(() => {
    if (status && investorsType.length === 0) {
      setFundTypeError(true)
    } else {
      setFundTypeError(false)
    }
  }, [investorsType, status])
  return (
    <>
      <Modal
        size='lg'
        show={showModal}
        onHide={() => {
          setShowModal(false)
          setStatus(false)
          setLoading(false)
        }}
        aria-labelledby='contained-modal-title-vcenter'
        centered
      >
        <Formik initialValues={initialValues} validationSchema={userSchema} onSubmit={onSubmit}>
          {(formik) => {
            return (
              <Form>
                <Modal.Header closeButton>
                  <h2>{formatMessage({ id: 'Create Investor' })}</h2>
                </Modal.Header>{' '}
                <Modal.Body>
                  <div className='d-md-flex col-md-12 flex-wrap w-100 m-auto justify-content-center'>
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Investor Name/Firm name' })}
                      fieldName={'name'}
                      formik={formik}
                      placeholder={formatMessage({ id: "Enter Investor's Name/Firm's Name" })}
                      toolTipText={formatMessage({ id: 'GLOBAL.TOOLTIP.GENERAL.INVESTOR_NAME' })}
                      margin='me-6'
                      width={5}
                      isStarRequired={true}
                      isTooltipForModal
                    />
                    <div className='mt-md-0 mt-6 me-6 multi-select-create'>
                      <label className='text-dark text-capitalize font-size-13 form-label '>
                        {formatMessage({ id: 'Investors Type*' })}
                      </label>
                      <ToolTipUIForModal
                        tooltipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.HIGHLIGHTS.INVESTOR_TYPE',
                        })}
                      />
                      <Select
                        isMulti
                        name='investorsType'
                        options={investorType}
                        className='basic-multi-select mb-7 mb-md-0 custom-select w-100'
                        onChange={(investorsType) => setInvestorsType(investorsType)}
                        placeholder={formatMessage({ id: 'Choose Investor type' })}
                        classNamePrefix='react-select'
                        required={true}
                      />
                      {fundTypeError && (
                        <span className='text-danger font-size-13'>
                          {formatMessage({ id: 'Fund Type is Required' })}
                        </span>
                      )}
                    </div>
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Contact Name' })}
                      fieldName={'contact_name'}
                      formik={formik}
                      placeholder={formatMessage({ id: 'Enter Contact Name' })}
                      toolTipText={formatMessage({ id: 'GLOBAL.TOOLTIP.GENERAL.CONTACT_NAME' })}
                      margin='me-6'
                      width={5}
                      isStarRequired={true}
                      isTooltipForModal
                    />
                    <TextInput
                      fieldType={'email'}
                      fieldName={'email'}
                      formik={formik}
                      placeholder={formatMessage({ id: 'Enter Email' })}
                      label={formatMessage({ id: 'Contact Email ID' })}
                      width={5}
                      toolTipText={formatMessage({ id: 'GLOBAL.TOOLTIP.PEOPLE.EMAIL' })}
                      isStarRequired={true}
                      isTooltipForModal
                    />
                  </div>
                  <div className='mt-2 d-md-flex justify-content-md-end me-md-6'>
                    <button
                      type='button'
                      className='btn btn-bg-light w-100 w-md-auto font-size-13'
                      style={{ height: '42px' }}
                      onClick={() => {
                        setShowModal(false)
                        setStatus(false)
                        setLoading(false)
                      }}
                    >
                      {formatMessage({ id: 'Cancel' })}
                    </button>
                    <CustomButton
                      isSubmitting={formik.isSubmitting}
                      isValid={formik.isValid}
                      buttonText={formatMessage({ id: 'Create Investor' })}
                      loading={loading}
                      widthLoading={2}
                      width={2}
                      height={44}
                      marginButtom={'mb-0 ms-md-3 mt-md-auto mt-2 mb-0'}
                    />
                  </div>
                </Modal.Body>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </>
  )
}
